import {
  CellClassParams,
  ColDef,
  IRowNode,
  ITooltipParams,
  ValueFormatterParams,
  ValueSetterParams,
} from '@ag-grid-community/core';
import { NewValueParams } from '@ag-grid-community/core/dist/esm/es6/entities/colDef';
import { set } from 'lodash-es';
import { firstValueFrom } from 'rxjs';

import { AgDeleteCellComponent } from '@features/inline-budget/ag-delete-cell.component';

import { AgRequiredHeaderComponent } from '@shared/ag-components/ag-required-header/ag-required-header.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import {
  ActivityType,
  BudgetActivityAttributes,
  Currency,
  listBudgetActivitiesQuery,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { RegExpUtils } from '@shared/utils/regExp.utils';
import { Utils } from '@shared/utils/utils';

import { getMapActivitiesAttributeColumns } from '../map-activities.constans';
import { AgBudgetActivityDropdownComponent } from './ag-budget-activity-dropdown/ag-budget-activity-dropdown.component';
import { AgExtendableOptionsDropdownComponent } from './ag-extendable-options-dropdown/ag-extendable-options-dropdown.component';
import {
  ActivityCategories,
  MapActivitiesGridRow,
  MapActivityType,
} from './map-activities-modal.model';

// eslint-disable-next-line max-lines-per-function
export const getMapActivitiesGridColumns = ({
  attributes,
  budgetActivityOptions,
  gridData,
  isInvalidRow,
  onCellValueChanged,
  overlayService,
  removeRow,
  updateTotals,
  validateCell,
  vendorCurrency,
}: {
  attributes: BudgetActivityAttributes[];
  budgetActivityOptions: listBudgetActivitiesQuery[];
  gridData: MapActivitiesGridRow[];
  isInvalidRow: (rowNode: CellClassParams) => boolean;
  onCellValueChanged: (params: NewValueParams) => void;
  overlayService: OverlayService;
  removeRow: (rowNode: IRowNode) => void;
  updateTotals: () => void;
  validateCell: (isInvalidValue: boolean, data: MapActivitiesGridRow, node: IRowNode) => void;
  vendorCurrency: Currency;
}): ColDef[] => {
  return [
    {
      cellClass: (params) => {
        if (params.node.rowPinned) {
          return ['text-left'];
        }

        return ['invoice-item', '!block', 'text-elypsis', 'text-left'];
      },
      cellRendererParams: () => ({
        validateCell: validateCell,
      }),
      cellRendererSelector: (params) => {
        if (params.node.rowPinned) {
          return {};
        }

        return { component: AgExtendableOptionsDropdownComponent, params };
      },
      comparator: (a, b) => {
        return Utils.localeAlphaNumSort(a || '', b || '');
      },
      editable: false,
      field: 'invoice_item_description',
      headerName: 'Invoice Item',
      minWidth: 240,
      onCellValueChanged: (params: NewValueParams) => {
        onCellValueChanged(params);
      },
      tooltipField: 'invoice_item_description',
    },
    {
      cellClass: ['!block', 'ag-cell-align-right', 'truncate'],
      cellClassRules: {
        'border-aux-error': (params: CellClassParams) =>
          !params.node.rowPinned &&
          (params.data?.isNewRow ? isInvalidRow(params) : true) &&
          !params.value,
      },
      cellDataType: 'number',
      field: 'amount',
      headerComponent: AgRequiredHeaderComponent,
      headerName: 'Total',
      minWidth: 130,
      onCellValueChanged: (params: NewValueParams) => {
        onCellValueChanged(params);
      },
      tooltipValueGetter: (params: ITooltipParams) =>
        params.data?.amount ? Utils.agCurrencyFormatter(params.data?.amount, vendorCurrency) : '',
      valueFormatter: (params) => {
        return Utils.agCurrencyFormatter(params, vendorCurrency);
      },
      valueParser: (params) => Utils.roundToNumber(params.newValue, 2),
    },
    {
      cellClass: ['budget-activity-item', '!block', 'text-elypsis', 'text-left'],
      cellClassRules: {
        'border-aux-error': (params: CellClassParams) => {
          return (
            !params.node.rowPinned &&
            (params.data?.isNewRow ? isInvalidRow(params) : true) &&
            !params.value
          );
        },
      },
      cellRendererParams: () => {
        return {
          options:
            budgetActivityOptions.map(({ activity_type, categories, id, name }, index) => ({
              activity_type,
              categories: categories || [],
              categoryTitles: getCategoryTitles(
                activity_type,
                categories || [],
                index !== 0 ? budgetActivityOptions[index - 1] : null
              ),
              label: name || '',
              path: getActivityPath(activity_type, categories || []),
              value: id,
            })) || [],
          updateTotals: updateTotals,
          validateCell: validateCell,
        };
      },
      cellRendererSelector: (params) => {
        if (params.node.rowPinned) {
          return {};
        }

        return { component: AgBudgetActivityDropdownComponent, params };
      },
      editable: false,
      field: 'activity_id',
      headerComponent: AgRequiredHeaderComponent,
      headerName: 'Budget Activity',
      minWidth: 190,
      onCellValueChanged: (params: NewValueParams) => {
        onCellValueChanged(params);
      },
      tooltipField: 'activity_name',
      valueSetter: (params: ValueSetterParams) => {
        const newValue = budgetActivityOptions.find(({ id, name }) =>
          RegExpUtils.isUUID(params.newValue) ? id === params.newValue : name === params.newValue
        );

        if (!newValue) {
          set(params.data, 'activity_id', null);
          set(params.data, 'activity_name', null);
          set(params.data, 'activity_type', null);
        } else {
          set(params.data, 'activity_id', newValue.id);
          set(params.data, 'activity_name', newValue.name);
          set(params.data, 'activity_type', newValue.activity_type);
        }

        return true;
      },
    },
    {
      cellClass: ['text-elypsis'],
      cellClassRules: {
        'ag-cell-not-edit-cell': (params: CellClassParams) => !params.node.rowPinned,
        'cursor-not-allowed': (params: CellClassParams) => !params.node.rowPinned,
      },
      editable: false,
      field: 'activity_type',
      headerName: 'Cost Category',
      minWidth: 130,
      suppressFillHandle: true,
      tooltipValueGetter: (params: ITooltipParams) =>
        params.data?.activity_type
          ? MapActivityType[params.data?.activity_type as keyof typeof MapActivityType]
          : '',
      valueFormatter: (params: ValueFormatterParams) => {
        return params.data?.activity_type
          ? MapActivityType[params.data?.activity_type as keyof typeof MapActivityType]
          : '';
      },
    },
    ...getMapActivitiesAttributeColumns(attributes, budgetActivityOptions, gridData),
    {
      cellClass: '!p-0 h-full',
      cellRendererParams: () => ({
        onDelete: async (params: NewValueParams) => {
          const data = params.node?.data;

          if (!data || !params.node) {
            return;
          }

          if (!data.isNewRow) {
            const ref = overlayService.openPopup({
              content: ConfirmationModalComponent,
              data: {
                message: 'Are you sure you want to remove this row?',
              },
              settings: {
                header: 'Remove Activity',
                primaryButton: {
                  label: 'Remove',
                },
              },
            });

            const event = await firstValueFrom(ref.afterClosed$);

            if (!event.data) {
              return;
            } else {
              removeRow(params.node);
            }
          } else {
            removeRow(params.node);
          }
        },
      }),
      cellRendererSelector: (params) => {
        if (params.node.rowPinned) {
          return {};
        }

        return { component: AgDeleteCellComponent, params };
      },
      editable: false,
      field: 'action',
      headerName: '',
      maxWidth: 32,
      minWidth: 32,
      pinned: 'right',
      resizable: false,
      width: 32,
    },
  ];
};

function getActivityPath(activityType: ActivityType, categories: ActivityCategories[]): string {
  const path: MapActivityType | string[] = [
    MapActivityType[activityType as keyof typeof MapActivityType],
  ];

  categories.forEach((category, index) => {
    const itemByIndex = categories.find((cat) => cat.index === index + 1);

    if (itemByIndex) {
      path.push(category.name);
    }
  });

  return path.join('/');
}

function getCategoryTitles(
  activityType: ActivityType,
  categories: ActivityCategories[],
  previousActivity: listBudgetActivitiesQuery | null
): { level: number; name: MapActivityType | string }[] {
  const titles: { level: number; name: MapActivityType | string }[] = [];

  if (previousActivity && activityType === previousActivity.activity_type) {
    categories.forEach((category, index) => {
      if (
        previousActivity.categories &&
        category.name !== previousActivity.categories[index]?.name
      ) {
        titles.push({ level: category.index, name: category.name });
      }
    });

    return titles;
  }

  titles.push({ level: 0, name: MapActivityType[activityType as keyof typeof MapActivityType] });

  categories.forEach((category) => {
    titles.push({ level: category.index, name: category.name });
  });

  return titles;
}
